import React, {useMemo} from 'react';
import {reactCreateMemo} from '../react-utils/react-utils';
import {
    ListView,
    listViewCreateDataSource,
    ListViewProps,
    ListViewSupportedHtmlAttributes,
    NavigationOrientation
} from '@wix/devzai-utils-react';
import {identity, OmitStrict, returnFalse} from '@wix/devzai-utils-common';

export const SelectionList = reactCreateMemo(function SelectionList<VALUES, ITEMS> (props: SelectionList.Props<VALUES, ITEMS>) {
    const {
        value,
        onChange,

        orientation = NavigationOrientation.Horizontal,
        items,
        valueFunction = identity,
        disabledFunction = returnFalse,
        listViewRef,

        renderItem,

        ...htmlAttributes
    } = props;

    const listViewDataSource = useMemo(() => {
        return listViewCreateDataSource(items, {
            idFunction: (item, index) => {
                const itemValue = valueFunction(item);

                if (typeof itemValue === 'string' || typeof itemValue === 'number') {
                    return itemValue;
                } else {
                    return `selection-list-item-${index}`
                }
            }
        })
    }, [items]);

    return (
        <ListView<ITEMS>
            {...htmlAttributes}
            orientation={orientation}
            isCyclic={true}
            ref={listViewRef}
            onItemAction={eventData => {
                const dataItem = eventData.dataItem;

                if(!disabledFunction(dataItem)) {
                    const value = valueFunction(dataItem);
                    if (value !== undefined) {
                        onChange(value, {
                            itemElement: eventData.element
                        });
                    }
                }
            }}
            renderItem={renderProps => {
                const dataItem = renderProps.dataItem;

                const itemValue = valueFunction(dataItem);

                const isSelected = value === itemValue;

                return renderItem({
                    ...renderProps,
                    isSelected: isSelected
                })
            }}
        >
            {listViewDataSource}
        </ListView>
    );
});

export namespace SelectionList {

    export type OnChangeContextArgs = {
        itemElement: Element;
    }

    export interface Props<VALUE, ITEM = VALUE> extends OmitStrict<ListViewSupportedHtmlAttributes, 'defaultValue'> {
        value: VALUE | null;
        onChange: (value: VALUE, contextArgs: OnChangeContextArgs) => void;
        renderItem: ListViewProps<ITEM>['renderItem'];
        orientation?: NavigationOrientation;
        listViewRef?: React.Ref<ListView<ITEM>>;

        items: ITEM[];
        valueFunction?: (item: ITEM) => (VALUE | undefined);
        disabledFunction?: (item: ITEM) => boolean;
    }
}
